import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { map, take } from 'rxjs';

/**
 * Guard to check if user is authenticated.
 * 
 * Note: Actual permission checks (read/edit access) are now done server-side
 * based on category ReaderRoles/EditorRoles. The server will return 403 Forbidden
 * if the user doesn't have access.
 * 
 * These guards only ensure the user is logged in before accessing wiki pages.
 */
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isLoading()) {
    return authService.checkAuthentication().pipe(
      map(() => {
        if (authService.isAuthenticated()) {
          return true;
        }
        router.navigate(['/']);
        return false;
      }),
      take(1)
    );
  }

  if (authService.isAuthenticated()) {
    return true;
  }
  
  router.navigate(['/']);
  return false;
};

/**
 * Guard for article creation - allows authenticated users.
 * Server will check if user has editor access to the target category.
 */
export const canCreateArticleGuard: CanActivateFn = authGuard;

/**
 * Guard for article editing - allows authenticated users.
 * Server will check if user has editor access to the article's category.
 */
export const canEditArticleGuard: CanActivateFn = authGuard;

/**
 * Guard for category creation - only Wiki Admins can create root categories.
 * For sub-categories, server checks if user has editor access to parent.
 */
export const canCreateCategoryGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isLoading()) {
    return authService.checkAuthentication().pipe(
      map(() => {
        // For root categories, must be admin
        // For sub-categories, server will check parent permissions
        if (authService.isAuthenticated()) {
          return true;
        }
        router.navigate(['/dashboard']);
        return false;
      }),
      take(1)
    );
  }

  if (authService.isAuthenticated()) {
    return true;
  }
  
  router.navigate(['/dashboard']);
  return false;
};

/**
 * Guard for category editing - allows authenticated users.
 * Server will check if user has editor access to the category.
 */
export const canEditCategoryGuard: CanActivateFn = authGuard;
